import React, { useEffect, useState } from "react";
import { api, fmtDate, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Printer, CheckCircle2, XCircle, Trash, PlusCircle, ClipboardList, Lock } from "lucide-react";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import ItemAutocomplete from "@/components/crm/ItemAutocomplete";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const emptyItem = { product_description: "", requested_qty: 1, approved_qty: null, unit: "Nos" };
const empty = {
  mr_no: "", date: new Date().toISOString().slice(0,10),
  project_id: "", customer_id: "", customer_order_no: "",
  contact_person: "", contact_number: "",
  transportation_terms: "", payment_terms: "",
  packaging_type: "", tentative_dispatch_date: "",
  customer_po_no: "", customer_po_date: "",
  bill_to_name: "",
  bill_to_address: "",
  bill_to_gstin: "",
  bill_to_mobile: "",
  ship_to_name: "",
  ship_to_address: "",
  bill_person_name: "", bill_person_contact: "",
  ship_person_name: "", ship_person_contact: "",
  items: [{...emptyItem}],
  notes: "",
};

export default function MaterialRequisition() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [company, setCompany] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [approveTarget, setApproveTarget] = useState(null);

  const load = async () => {
    setItems((await api.get("/mrn")).data);
    try { setProjects((await api.get("/projects")).data); } catch { setProjects([]); }
    try { setCustomers((await api.get("/customers")).data); } catch { setCustomers([]); }
    try { setCompany((await api.get("/settings/company")).data); } catch { setCompany(null); }
  };
  useEffect(() => { load(); }, []);

  const num = (v) => parseFloat(v) || 0;
  const addItem = () => setForm({ ...form, items: [...form.items, {...emptyItem}] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_,idx)=>idx!==i) });
  const setItem = (i, k, v) => { const arr = [...form.items]; arr[i] = {...arr[i], [k]:v}; setForm({...form, items:arr}); };

  const pickCustomer = (cid) => {
    if (!cid) { setForm(f => ({...f, customer_id: "", bill_to_name: "", bill_to_address: "", bill_to_gstin: "", bill_to_mobile: "", ship_to_name: "", ship_to_address: ""})); return; }
    const c = customers.find(x => x.id === cid);
    if (!c) return;
    const addr = [c.address, c.city].filter(Boolean).join(", ");
    setForm(f => ({
      ...f, customer_id: cid,
      bill_to_name: c.name || "",
      bill_to_address: addr,
      bill_to_gstin: c.gstin || "",
      bill_to_mobile: c.mobile || "",
      ship_to_name: f.ship_to_name || c.name || "",
      ship_to_address: f.ship_to_address || addr,
      contact_person: f.contact_person || c.name || "",
      contact_number: f.contact_number || c.mobile || "",
    }));
  };

  const save = async () => {
    if (!form.customer_id) { toast.error("Bill To ke liye Customer select karo"); return; }
    try {
      const payload = { ...form };
      if (!payload.project_id) delete payload.project_id;
      // Supplier is ALWAYS the company (MRM RENEWABLES) — locked from settings
      if (company) {
        payload.supplier_name = company.company_name || "";
        payload.supplier_address = company.address || "";
      }
      if (editId) await api.patch(`/mrn/${editId}`, payload);
      else await api.post("/mrn", payload);
      toast.success(editId ? "MRN updated" : "MRN created — pending admin approval");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (m) => {
    setForm({ ...empty, ...m, project_id: m.project_id || "", customer_id: m.customer_id || "", items: (m.items||[]).map(it=>({...emptyItem, ...it})) });
    setEditId(m.id); setOpen(true);
  };
  const del = async (m) => { try { await api.delete(`/mrn/${m.id}`); toast.success("Deleted"); load(); } catch(e){ toast.error(formatError(e.response?.data?.detail)); } };

  const doApprove = async () => {
    try {
      await api.post(`/mrn/${approveTarget.id}/approve`, { items: approveTarget.items, remark: approveTarget._remark });
      toast.success(`MRN ${approveTarget.mr_no} approved`);
      setApproveTarget(null); load();
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };
  const doReject = async () => {
    if (!window.confirm(`Reject MRN ${approveTarget?.mr_no}?`)) return;
    try {
      await api.post(`/mrn/${approveTarget.id}/reject`, { items: [], remark: approveTarget._remark || "Rejected" });
      toast.success(`MRN ${approveTarget.mr_no} rejected`);
      setApproveTarget(null); load();
    } catch(e){ toast.error(formatError(e.response?.data?.detail)); }
  };

  const visible = items.filter(m => matchesQuery(m, search));
  const statusBadge = (s) => {
    const map = { Pending:"bg-amber-100 text-amber-800", Approved:"bg-emerald-100 text-emerald-800", Rejected:"bg-rose-100 text-rose-800" };
    return <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${map[s]||"bg-slate-100 text-slate-700"}`}>{s}</span>;
  };

  return (
    <div className="space-y-5" data-testid="mrn-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Material Requisition <span className="text-slate-400 text-lg font-medium">/ MRN</span></h1>
          <p className="text-slate-500 text-sm">Supplier: MRM (permanent) · Bill&nbsp;To / Ship&nbsp;To: Customer details auto-fill.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search MR No / customer..." testid="mrn-search-input"/>
          <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if(!v){ setForm(empty); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-mrn-btn"><Plus className="w-4 h-4 mr-1"/>New MRN</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><ClipboardList className="w-5 h-5 text-amber-600"/>{editId?"Edit":"New"} Material Requisition Note</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                {/* SUPPLIER — LOCKED FROM COMPANY SETTINGS */}
                <Section title="Supplier Details — Auto-locked from Company Settings">
                  <div className="border border-slate-300 bg-slate-50 rounded-md p-3 text-[12px] leading-relaxed" data-testid="mrn-supplier-locked">
                    <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-emerald-700 mb-1">
                      <Lock className="w-3 h-3"/> Permanent — Change karne ke liye Settings → Company Profile me update karo
                    </div>
                    <div className="font-black text-slate-900">{company?.company_name || "MRM RENEWABLES ENERGY PRIVATE LIMITED"}</div>
                    <div className="text-slate-700 whitespace-pre-wrap">{company?.address || "—"}</div>
                    <div className="text-slate-700 mt-1">
                      Contact No. <b>+91 {company?.phone || ""}</b> · e-mail: <b>{company?.email || ""}</b>
                    </div>
                    <div className="text-slate-700 text-[11px]">
                      CIN: <b className="font-mono">{company?.cin || ""}</b> · Udyam: <b className="font-mono">{company?.udyam || ""}</b> · GSTIN: <b className="font-mono">{company?.gstin || ""}</b>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                    <div><Label className="text-xs font-semibold text-slate-600">MR No. (auto if blank)</Label>
                      <Input value={form.mr_no} onChange={e=>setForm({...form, mr_no:e.target.value})} placeholder="Auto (MR26_0001)" data-testid="mrn-mr-no"/></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Date</Label>
                      <Input type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})}/></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Customer Order No.</Label>
                      <Input value={form.customer_order_no} onChange={e=>setForm({...form, customer_order_no:e.target.value})} placeholder="e.g. PROJ26_0007" data-testid="mrn-customer-order"/></div>
                  </div>
                </Section>

                {/* BILL TO — CUSTOMER (auto-fill on select) */}
                <Section title="Bill To — Customer Details (Customer select karo, auto fill hoga)">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">Customer* <span className="text-rose-600">(required)</span></Label>
                      <Select value={form.customer_id || "__NONE__"} onValueChange={v=>pickCustomer(v==="__NONE__" ? "" : v)}>
                        <SelectTrigger data-testid="mrn-customer-select"><SelectValue placeholder="Select customer"/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__NONE__">— Select Customer —</SelectItem>
                          {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} — {c.mobile}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Bill To Name</Label>
                      <Input value={form.bill_to_name} onChange={e=>setForm({...form, bill_to_name:e.target.value})} placeholder="(Customer name)" data-testid="mrn-bill-name"/>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">GSTIN (Customer)</Label>
                      <Input value={form.bill_to_gstin} onChange={e=>setForm({...form, bill_to_gstin:e.target.value})} placeholder="Customer GSTIN (if any)" data-testid="mrn-bill-gstin"/>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">Bill To Address</Label>
                      <Textarea rows={2} value={form.bill_to_address} onChange={e=>setForm({...form, bill_to_address:e.target.value})} placeholder="Customer address" data-testid="mrn-bill-address"/>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Contact Person</Label>
                      <Input value={form.bill_person_name} onChange={e=>setForm({...form, bill_person_name:e.target.value})} data-testid="mrn-bill-person"/>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Contact No.</Label>
                      <Input value={form.bill_person_contact} onChange={e=>setForm({...form, bill_person_contact:e.target.value})} data-testid="mrn-bill-contact"/>
                    </div>
                  </div>
                </Section>

                {/* SHIP TO — Editable (defaults to customer address) */}
                <Section title="Ship To — Site / Delivery Address">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Ship To Name</Label>
                      <Input value={form.ship_to_name} onChange={e=>setForm({...form, ship_to_name:e.target.value})} placeholder="Customer / Site name" data-testid="mrn-ship-name"/>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Site Contact + No.</Label>
                      <div className="flex gap-2">
                        <Input placeholder="Name" value={form.ship_person_name} onChange={e=>setForm({...form, ship_person_name:e.target.value})}/>
                        <Input placeholder="Phone" value={form.ship_person_contact} onChange={e=>setForm({...form, ship_person_contact:e.target.value})}/>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-semibold text-slate-600">Ship To Address (site / project location — editable)</Label>
                      <Textarea rows={2} value={form.ship_to_address} onChange={e=>setForm({...form, ship_to_address:e.target.value})} placeholder="Project site address ya same as billing" data-testid="mrn-ship-address"/>
                    </div>
                  </div>
                </Section>

                <Section title="Terms">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs font-semibold text-slate-600">Transportation Terms</Label>
                      <Input value={form.transportation_terms} onChange={e=>setForm({...form, transportation_terms:e.target.value})}/></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Payment Terms</Label>
                      <Input value={form.payment_terms} onChange={e=>setForm({...form, payment_terms:e.target.value})}/></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Packaging Type</Label>
                      <Input value={form.packaging_type} onChange={e=>setForm({...form, packaging_type:e.target.value})}/></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Tentative Dispatch Date</Label>
                      <Input type="date" value={form.tentative_dispatch_date} onChange={e=>setForm({...form, tentative_dispatch_date:e.target.value})}/></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Customer PO No.</Label>
                      <Input value={form.customer_po_no} onChange={e=>setForm({...form, customer_po_no:e.target.value})}/></div>
                    <div><Label className="text-xs font-semibold text-slate-600">Customer PO Date</Label>
                      <Input type="date" value={form.customer_po_date} onChange={e=>setForm({...form, customer_po_date:e.target.value})}/></div>
                  </div>
                </Section>

                <Section title="Products Requested">
                  <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1 grid grid-cols-12 gap-2">
                    <div className="col-span-1">Sr</div>
                    <div className="col-span-6">Product Description</div>
                    <div className="col-span-2 text-right">Requested Qty</div>
                    <div className="col-span-2">Unit</div>
                    <div className="col-span-1"></div>
                  </div>
                  {form.items.map((it,i)=>(
                    <div key={i} className="grid grid-cols-12 gap-2 items-center py-1">
                      <div className="col-span-1 text-slate-500 font-mono text-sm">{i+1}</div>
                      <div className="col-span-6">
                        <ItemAutocomplete
                          value={it.product_description}
                          onChange={(n)=>setItem(i,"product_description",n)}
                          onPick={(picked)=>{
                            const arr=[...form.items];
                            arr[i]={...arr[i], product_description:picked.name,
                              unit: picked.unit||arr[i].unit};
                            setForm({...form, items:arr});
                          }}
                          placeholder="e.g. Waaree Bifacial Panel 585W"
                          testid={`mrn-item-desc-${i}`}
                        /></div>
                      <div className="col-span-2"><Input type="number" step="0.01" value={it.requested_qty} onChange={e=>setItem(i,"requested_qty",num(e.target.value))} className="text-right" data-testid={`mrn-item-qty-${i}`}/></div>
                      <div className="col-span-2"><Input value={it.unit||""} onChange={e=>setItem(i,"unit",e.target.value)} placeholder="Nos / m / kg"/></div>
                      <div className="col-span-1 text-right">{form.items.length>1 && <Button type="button" size="icon" variant="ghost" onClick={()=>rmItem(i)} className="text-rose-600 h-8 w-8"><Trash className="w-3.5 h-3.5"/></Button>}</div>
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="mrn-add-item" className="mt-2"><PlusCircle className="w-3.5 h-3.5 mr-1"/>Add Row</Button>
                </Section>
                <Section title="Notes"><Textarea rows={2} value={form.notes||""} onChange={e=>setForm({...form, notes:e.target.value})} data-testid="mrn-notes"/></Section>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} disabled={!form.customer_id || form.items.length===0} data-testid="save-mrn-btn">{editId?"Update":"Submit for Approval"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>MR No.</TableHead><TableHead>Date</TableHead><TableHead>Bill To (Customer)</TableHead>
            <TableHead>Items</TableHead><TableHead>Status</TableHead>
            <TableHead>Created By</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {visible.length===0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">{search?`No matches`:"No MRNs yet."}</TableCell></TableRow>}
            {visible.map(m=>(
              <TableRow key={m.id} data-testid={`mrn-row-${m.mr_no}`}>
                <TableCell className="font-mono text-xs font-semibold">{m.mr_no}</TableCell>
                <TableCell className="text-xs">{fmtDate(m.date || m.created_at)}</TableCell>
                <TableCell>{m.bill_to_name || "-"}<div className="text-xs text-slate-500">{m.bill_person_contact || m.bill_to_mobile || ""}</div></TableCell>
                <TableCell className="text-xs">{(m.items||[]).length}</TableCell>
                <TableCell>{statusBadge(m.status)}</TableCell>
                <TableCell className="text-xs">{m.created_by_name||"-"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Link to={`/material-requisition/${m.id}/print`}>
                      <Button size="sm" variant="outline" data-testid={`print-mrn-${m.mr_no}`}><Printer className="w-3 h-3 mr-1"/>Print</Button>
                    </Link>
                    <Button size="sm" variant="ghost" onClick={()=>edit(m)} data-testid={`edit-mrn-${m.mr_no}`}><Pencil className="w-3.5 h-3.5"/></Button>
                    {user?.role === "admin" && m.status === "Pending" && (
                      <Button size="sm" variant="outline" onClick={()=>setApproveTarget({ ...m, _remark: "" })} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`approve-mrn-${m.mr_no}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Approve
                      </Button>
                    )}
                    {user?.role === "admin" && <DeleteButton title={`Delete MRN ${m.mr_no}?`} description="This cannot be undone." onConfirm={()=>del(m)} testid={`delete-mrn-${m.mr_no}`}/>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Approve dialog */}
      <Dialog open={!!approveTarget} onOpenChange={(v)=>{ if(!v) setApproveTarget(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Approve MRN — {approveTarget?.mr_no}</DialogTitle></DialogHeader>
          {approveTarget && (
            <div className="space-y-4">
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                Bill To: <b>{approveTarget.bill_to_name}</b> · Requested by <b>{approveTarget.created_by_name}</b> on {fmtDate(approveTarget.created_at)}
              </div>
              <div className="grid grid-cols-12 gap-2 text-[10px] uppercase font-bold text-slate-500">
                <div className="col-span-1">Sr</div>
                <div className="col-span-5">Description</div>
                <div className="col-span-2 text-right">Req Qty</div>
                <div className="col-span-2 text-right">Approved Qty</div>
                <div className="col-span-2">Unit</div>
              </div>
              {(approveTarget.items||[]).map((it,i)=>(
                <div key={i} className="grid grid-cols-12 gap-2 items-center py-1 border-t border-slate-100">
                  <div className="col-span-1 font-mono">{it.sr_no||i+1}</div>
                  <div className="col-span-5 text-sm">{it.product_description}</div>
                  <div className="col-span-2 text-right font-mono">{it.requested_qty}</div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" value={it.approved_qty ?? it.requested_qty} onChange={e=>{
                      const arr = [...approveTarget.items]; arr[i] = {...arr[i], approved_qty:parseFloat(e.target.value)||0}; setApproveTarget({...approveTarget, items:arr});
                    }} className="text-right h-8" data-testid={`approve-qty-${i}`}/>
                  </div>
                  <div className="col-span-2 text-xs">{it.unit||"Nos"}</div>
                </div>
              ))}
              <div><Label className="text-xs font-semibold text-slate-600">Approval Remark (optional)</Label>
                <Textarea rows={2} value={approveTarget._remark||""} onChange={e=>setApproveTarget({...approveTarget, _remark:e.target.value})} data-testid="approve-remark"/></div>
            </div>
          )}
          <DialogFooter className="flex justify-between">
            <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={doReject} data-testid="reject-mrn-btn"><XCircle className="w-4 h-4 mr-1"/>Reject</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={()=>setApproveTarget(null)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doApprove} data-testid="confirm-approve-mrn"><CheckCircle2 className="w-4 h-4 mr-1"/>Approve MRN</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 border-b border-slate-200 pb-1 mb-2">{title}</div>
      {children}
    </div>
  );
}
